import { A11yModule, FocusOrigin } from '@angular/cdk/a11y'
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  linkedSignal,
  viewChild,
} from '@angular/core'
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms'
import { NewsletterType, pickPropsAssertDefined } from '@pai/commons'

import { AnalyticsService } from 'app/core/analytics/analytics.service'
import { AnalyticsEvent } from 'app/core/analytics/analytics-event.enum'
import { FormsApi } from 'app/core/forms-api'
import { CURRENT_LANG } from 'app/core/i18n/language.token'
import { LoggerService } from 'app/core/logger/logger.service'
import { PaiValidators } from 'app/utils/forms/pai-validators'
import { injectApprovalResource } from 'app/utils/inject-approval-resource.function'

import { InputFieldComponent } from '../input-field/input-field.component'
import { RippleDirective } from '../ripple/ripple.directive'
import { IconComponent } from '../svg/icon.component'
import { TranslatePipe } from '../translate.pipe'

export type MessageType = 'validationError' | 'hint' | 'success' | 'requestError'

@Component({
  selector: 'pai-newsletter-subscription',
  templateUrl: './newsletter-subscription.component.html',
  styleUrls: ['./newsletter-subscription.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, InputFieldComponent, IconComponent, RippleDirective, A11yModule, TranslatePipe],
})
export class NewsletterSubscriptionComponent {
  private static instanceCounter = 0

  protected get hintId(): string {
    return `newsletter-subscription-${this.instanceNum}__hint`
  }

  protected readonly hintElRef = viewChild.required('hintElRef', { read: ElementRef<HTMLDivElement> })
  protected readonly inputField = viewChild.required(InputFieldComponent)
  protected readonly message = linkedSignal<boolean, MessageType>({
    source: computed(() => this.inputField()?.isActive() ?? false),
    computation: (isActive, prev): MessageType => {
      if (isActive) {
        return 'hint'
      } else {
        return prev?.value ?? 'hint'
      }
    },
  })

  protected readonly subscriptionGroup = new FormGroup({
    email: new FormControl(null, [PaiValidators.requiredString, PaiValidators.email]),
  })
  private readonly instanceNum = ++NewsletterSubscriptionComponent.instanceCounter
  private readonly logger = inject(LoggerService).getInstance('NewsletterSubscriptionComponent')
  private readonly analytics = inject(AnalyticsService)
  private readonly formsApi = inject(FormsApi)
  private readonly currentLanguage = inject(CURRENT_LANG)
  private readonly approval = injectApprovalResource()

  protected onFormFocusChange(event: FocusOrigin) {
    const emailCtrl = this.subscriptionGroup.controls.email
    if (event === null && (emailCtrl.value === '' || emailCtrl.value === null)) {
      // without setTimeout the form reset is done before the FormControl handles blur (and would set it again to touched)
      setTimeout(this.resetForm.bind(this))
    }
  }

  protected onSubmit(emailField: HTMLInputElement) {
    if (!this.subscriptionGroup.valid || this.approval.isLoading()) {
      this.subscriptionGroup.controls.email.markAsTouched()
      emailField.focus()
      return
    }

    this.formsApi
      .sendNewsletterSubscription({
        type: NewsletterType.EMAIL,
        ...pickPropsAssertDefined(this.subscriptionGroup.getRawValue(), 'email'),
        // todo open modal for given name and family name
        givenName: 'n/a',
        familyName: 'n/a',
        language: this.currentLanguage,
        comment: null,
        approval: this.approval.value(),
      })
      .then(this.onSuccess.bind(this), this.onError.bind(this))
    this.analytics.trackEvent(AnalyticsEvent.NEWSLETTER_SUBSCRIPTION_FORM_SUBMIT)
  }

  private onSuccess() {
    this.resetForm()
    this.message.set('success')
    this.hintElRef().nativeElement.focus()
  }

  private onError(err: any) {
    this.logger.error(`submitting Form for failed`, err, { value: this.subscriptionGroup.value })
    this.message.set('requestError')
    this.hintElRef().nativeElement.focus()
  }

  private resetForm() {
    this.subscriptionGroup.reset()
  }
}
